import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle system
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      opacity: number;
      pulseSpeed: number;
      pulsePhase: number;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        const colors = ['#14b8a6', '#06b6d4', '#8b5cf6', '#a855f7'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.opacity = Math.random() * 0.5 + 0.3;
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;

        this.pulsePhase += this.pulseSpeed;
      }

      draw() {
        if (!ctx) return;
        const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * pulse, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity * pulse;
        ctx.fill();
        ctx.globalAlpha = 1;

        // Glow effect
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * pulse * 2, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * pulse * 2);
        gradient.addColorStop(0, this.color + '40');
        gradient.addColorStop(1, this.color + '00');
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    }

    // Circuit lines
    class Circuit {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      progress: number;
      speed: number;
      color: string;
      width: number;

      constructor() {
        this.x1 = Math.random() * canvas.width;
        this.y1 = Math.random() * canvas.height;
        const angle = Math.random() * Math.PI * 2;
        const length = Math.random() * 150 + 50;
        this.x2 = this.x1 + Math.cos(angle) * length;
        this.y2 = this.y1 + Math.sin(angle) * length;
        this.progress = 0;
        this.speed = Math.random() * 0.01 + 0.005;
        const colors = ['#14b8a6', '#06b6d4', '#8b5cf6'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.width = Math.random() * 2 + 1;
      }

      update() {
        this.progress += this.speed;
        if (this.progress > 1) {
          this.progress = 0;
          this.x1 = Math.random() * canvas.width;
          this.y1 = Math.random() * canvas.height;
          const angle = Math.random() * Math.PI * 2;
          const length = Math.random() * 150 + 50;
          this.x2 = this.x1 + Math.cos(angle) * length;
          this.y2 = this.y1 + Math.sin(angle) * length;
        }
      }

      draw() {
        if (!ctx) return;
        const currentX = this.x1 + (this.x2 - this.x1) * this.progress;
        const currentY = this.y1 + (this.y2 - this.y1) * this.progress;

        ctx.beginPath();
        ctx.moveTo(this.x1, this.y1);
        ctx.lineTo(currentX, currentY);
        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.width;
        ctx.globalAlpha = 0.6;
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Draw node at end
        ctx.beginPath();
        ctx.arc(currentX, currentY, 3, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();

        // Glow for node
        ctx.beginPath();
        ctx.arc(currentX, currentY, 6, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(currentX, currentY, 0, currentX, currentY, 6);
        gradient.addColorStop(0, this.color + '80');
        gradient.addColorStop(1, this.color + '00');
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    }

    const particles: Particle[] = [];
    const circuits: Circuit[] = [];

    // Create particles
    for (let i = 0; i < 80; i++) {
      particles.push(new Particle());
    }

    // Create circuits
    for (let i = 0; i < 25; i++) {
      circuits.push(new Circuit());
    }

    // Animation loop
    function animate() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw circuits
      circuits.forEach(circuit => {
        circuit.update();
        circuit.draw();
      });

      // Draw particles
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Connect nearby particles
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = '#14b8a6';
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = (1 - distance / 120) * 0.3;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        });
      });

      requestAnimationFrame(animate);
    }

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const handleScrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleWizeAIClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // First scroll to top
    window.scrollTo(0, 0);
    // Then navigate
    window.REACT_APP_NAVIGATE('/wizeai');
    // Ensure scroll to top after navigation
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
  };

  return (
    <section 
      className="relative min-h-screen flex items-center pt-20 bg-gray-950 overflow-hidden"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/7090a2bbe5467a364bd98914cfc467f8.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-gray-950/95 via-gray-900/90 to-gray-950/80"></div>
      
      {/* Animated Canvas */}
      <canvas 
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ mixBlendMode: 'screen' }}
      />
      
      <div className="relative w-full mx-auto px-6 lg:px-16 py-20 z-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-8 animate-pulse">
            <i className="ri-sparkling-line text-teal-400"></i>
            <span className="text-sm text-teal-300">AI-Powered Analytics Platform</span>
          </div>
          
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Transform Data Into Strategic Insights
          </h1>
          
          <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
            Harness the power of enterprise-grade analytics to make data-driven decisions faster. Real-time insights, predictive modeling, and seamless integration.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={handleScrollToPricing}
              className="px-8 py-4 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap shadow-xl hover:shadow-teal-500/50 hover:scale-105 transform"
            >
              Start Free Trial
            </button>
            <Link 
              to="/wizeai"
              className="px-8 py-4 bg-transparent text-white rounded-lg font-semibold border-2 border-gray-600 hover:bg-gray-800/50 hover:border-teal-400 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-robot-2-line text-xl"></i>
              WizeAI
            </Link>
          </div>
          
          <div className="flex items-center gap-8 mt-12">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-2xl text-teal-400"></i>
              <span className="text-gray-300">No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-2xl text-teal-400"></i>
              <span className="text-gray-300">14-day free trial</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}